import React, { useState } from 'react';
import { Form, Input, Button, Upload, message, Row, Col, Image } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import axiosInstance from '../utils/axiosInstance';

interface CreateReportingOfficerProps {
    onSuccess?: () => void;
}

const CreateReportingOfficer: React.FC<CreateReportingOfficerProps> = ({ onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    const onFinish = async (values: any) => {
        console.log('Form values:', values); // For debugging
        setLoading(true);

        const formData = new FormData();
        formData.append('officer_name', values.officer_name);
        formData.append('email', values.email);
        formData.append('phone_number', values.phone_number);

        if (values.img && values.img.length > 0) {
            formData.append('img', values.img[0].originFileObj);
        }

        try {
            await axiosInstance.post('/officer', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            message.success('Officer created successfully');
            form.resetFields();
            setImagePreview(null);

            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            if (error.response && error.response.data.detail) {
                message.error(error.response.data.detail);
            } else {
                message.error('Failed to create officer');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle image preview
    const handleImageChange = (info: any) => {
        // Get the uploaded file
        const file = info.fileList[0]?.originFileObj;
        if (file) {
            // Create a preview URL
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            setImagePreview(null);
        }
    };

    // Helper function to normalize the file input
    const normFile = (e: any) => {
        console.log('Upload event:', e); // For debugging
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    {/* Officer Name */}
                    <Form.Item
                        label="Officer Name"
                        name="officer_name"
                        rules={[{ required: true, message: 'Please input the officer name!' }]}
                    >
                        <Input placeholder="Enter officer's name" />
                    </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                    {/* Email */}
                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                            { required: true, message: 'Please input the email!' },
                            { type: 'email', message: 'Please enter a valid email!' },
                        ]}
                    >
                        <Input placeholder="Enter email" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    {/* Phone Number */}
                    <Form.Item
                        label="Phone Number"
                        name="phone_number"
                        rules={[{ required: true, message: 'Please input the phone number!' }]}
                    >
                        <Input placeholder="Enter phone number" />
                    </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                    {/* Profile Image */}
                    <Form.Item
                        label="Profile Image"
                        name="img"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                    >
                        <Upload
                            listType="picture-card"
                            accept="image/*"
                            maxCount={1}
                            beforeUpload={() => false}
                            onChange={handleImageChange}
                        >
                            {imagePreview ? null : (
                                <div>
                                    <UploadOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>
                </Col>
            </Row>

            {/* Image Preview */}
            {imagePreview && (
                <Row gutter={16}>
                    <Col xs={24}>
                        <Image
                            src={imagePreview}
                            alt="Profile Preview"
                            style={{ maxWidth: '200px', borderRadius: '8px' }}
                        />
                    </Col>
                </Row>
            )}

            {/* Submit Button */}
            <Form.Item style={{ marginTop: '16px' }}>
                <Button type="primary" htmlType="submit" loading={loading}>
                    Create Officer
                </Button>
            </Form.Item>
        </Form>
    );
};

export default CreateReportingOfficer;